import { AdminTeams } from '@platform/client';
import {
  AdminTeamMatchSummary,
  AdminTeamMember,
  AdminTeamMemberDetailed,
  SkillCategoriesEnum,
  TeamSkillProficiency,
  TeamSkillProficiencyProficiencyCounts,
  UserTeamAssignmentStatusEnum,
  MatchProgressEnum,
} from '@platform/client/schemas';
import { Button, Icon, StandardButtonProps } from '@platform/components';
import { useState } from 'react';
import { Link, useParams } from 'wouter';
import { EditMembersModal } from './modals/EditMembersModal';
import { AriaButtonProps } from 'react-aria';
import { ProjectAssignmentModal } from './modals/ProjectAssignmentModal';
import { MemberSkillsChunked } from './TeamsResultsTab';
import { ApproveTeamModal } from './modals/ApproveTeamModal';
import { formatDateStr } from '../../../dashboard/my-projects/utils';
import { ProjectLink } from '../projects/ProjectsAdmin';
import { ProfessionalLink } from '../professionals/ProfessionalsAdmin';
import { MatchGroupLink } from '../matches/MatchesAdmin';

export const TeamDetailPage = () => {
  const { teamId, cohortId } = useParams<{ teamId: string; cohortId: string }>();

  const [editMembersModalOpen, setEditMembersModalOpen] = useState<{
    isOpen: boolean;
    teamId: undefined | number;
    incomingNewMembers: undefined | AdminTeamMember[];
  }>({ isOpen: false, teamId: undefined, incomingNewMembers: undefined });
  const [editProjectAssignmentData, setEditProjectAssignmentData] = useState<{
    teamId: number;
    projectId?: number;
    currentMatchGroupId?: number;
    currentMatchProgress?: MatchProgressEnum;
  }>();
  const [approvedMembersModalOpen, setApprovedMembersModalOpen] = useState(false);

  const { data } = AdminTeams.useGetTeamDetails({ team_id: Number(teamId) });

  return (
    <div className='w-full md:max-w-[1280px] mx-auto px-4 md:px-20 text-sm text-gray-700'>
      <H1>
        Team [id: {teamId}] -{' '}
        <Link href={`/${cohortId}`} className='blue-link'>
          {data?.team.cohortName}
        </Link>
      </H1>
      {data ?
        <>
          <AssignedProjectSection
            teamStatus={data.team.status}
            teamId={parseInt(teamId)}
            matchData={data.team.matchData}
            setEditProjectAssignmentData={setEditProjectAssignmentData}
          />
          <SkillsSection
            categories={data.team.categories}
            skillsProficiencies={data.team.skillsProficiencies}
          />
          <TeamMembersSection
            teamId={parseInt(teamId)}
            teamStatus={data.team.status}
            members={data.team.members}
            setEditMembersModalOpen={setEditMembersModalOpen}
            setApprovedMembersModalOpen={setApprovedMembersModalOpen}
          />
        </>
      : <p>Loading...</p>}
      {editMembersModalOpen.isOpen && editMembersModalOpen.teamId && (
        <EditMembersModal
          teamId={editMembersModalOpen.teamId}
          cohortId={Number(cohortId)}
          onClose={() => {
            setEditMembersModalOpen({
              isOpen: false,
              teamId: undefined,
              incomingNewMembers: undefined,
            });
          }}
        />
      )}
      {editProjectAssignmentData && (
        <ProjectAssignmentModal
          cohortId={parseInt(cohortId)}
          teamId={editProjectAssignmentData.teamId}
          currentProjectId={editProjectAssignmentData.projectId}
          currentMatchGroupId={editProjectAssignmentData.currentMatchGroupId}
          currentMatchProgress={editProjectAssignmentData.currentMatchProgress}
          onClose={() => {
            setEditProjectAssignmentData(undefined);
          }}
        />
      )}
      {approvedMembersModalOpen && data && (
        <ApproveTeamModal
          teamIds={[parseInt(teamId)]}
          cohortId={parseInt(cohortId)}
          isOpen={true}
          onClose={() => setApprovedMembersModalOpen(false)}
        />
      )}
    </div>
  );
};

const TeamMembersSection = ({
  teamId,
  members,
  teamStatus,
  setEditMembersModalOpen,
  setApprovedMembersModalOpen,
}: {
  teamId: number;
  members: AdminTeamMemberDetailed[];
  teamStatus: UserTeamAssignmentStatusEnum;
  setEditMembersModalOpen: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      teamId: undefined | number;
      incomingNewMembers: undefined | AdminTeamMember[];
    }>
  >;
  setApprovedMembersModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const sectionTitle =
    teamStatus === UserTeamAssignmentStatusEnum.proposed ? 'Proposed Team Members' : 'Team Members';
  const sectionCtas = [
    {
      text: 'Edit',
      onPress: () =>
        setEditMembersModalOpen({
          isOpen: true,
          teamId: teamId,
          incomingNewMembers: undefined,
        }),
    },
  ];
  if (teamStatus === UserTeamAssignmentStatusEnum.proposed) {
    sectionCtas.push({
      text: 'Approve Members',
      onPress: () => setApprovedMembersModalOpen(true),
    });
  }
  return (
    <PageSection title={sectionTitle} sectionCtas={sectionCtas}>
      <div className='grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-4'>
        {members.map((member) => (
          <TeamMemberCard key={member.id} member={member} />
        ))}
      </div>
    </PageSection>
  );
};

const TeamMemberCard = ({ member }: { member: AdminTeamMemberDetailed }) => {
  return (
    <div className='flex flex-col gap-2 items-center text-center border p-4 rounded mb-2 shadow-xs'>
      <H3>
        <ProfessionalLink userId={member.id} name={member.name} />
      </H3>
      <p>
        {member.city}, {member.country}
      </p>
      <div className='flex gap-2 items-end'>
        <H4>{member.jobTitle}</H4>
        {member.linkedinUrl && (
          <a href={member.linkedinUrl} target='_blank'>
            <Icon type='linked-in' size='20' additionalClassNames='blue-link mb-1' />
          </a>
        )}
        {member.resume && (
          <a href={member.resume} target='_blank' className='text-blue-600 underline'>
            <Icon type='file-06' size='20' additionalClassNames='blue-link mb-1' />
          </a>
        )}
      </div>
      <MemberSkillsChunked skills={member.skills} />
      {member.skillsExplanation && (
        <p>
          <em>"{member.skillsExplanation}"</em>
        </p>
      )}
    </div>
  );
};

const AssignedProjectSection = ({
  teamId,
  teamStatus,
  matchData,
  setEditProjectAssignmentData,
}: {
  teamId: number;
  teamStatus: UserTeamAssignmentStatusEnum;
  setEditProjectAssignmentData: React.Dispatch<
    React.SetStateAction<
      | {
          teamId: number;
          projectId?: number;
          currentMatchGroupId?: number;
          currentMatchProgress?: MatchProgressEnum;
        }
      | undefined
    >
  >;
  matchData?: AdminTeamMatchSummary | null;
}) => {
  return (
    <PageSection
      title='Assigned Project'
      sectionCtas={[
        {
          text: 'Edit',
          onPress: () =>
            setEditProjectAssignmentData({
              teamId: teamId,
              projectId: matchData?.projectId ?? undefined,
              currentMatchGroupId: matchData?.matchGroupId,
              currentMatchProgress: matchData?.progress,
            }),
          isDisabled: teamStatus !== UserTeamAssignmentStatusEnum.approved,
        },
      ]}
    >
      {teamStatus === UserTeamAssignmentStatusEnum.proposed ?
        <p className='italic'>Assign after team is approved</p>
      : matchData ?
        <div>
          <ProjectLink projectId={matchData.projectId} projectName={matchData.projectName} />
          <p>
            {matchData.progress.charAt(0).toUpperCase() + matchData.progress.slice(1)}{' '}
            {formatDateStr(matchData.progressDate)}
          </p>
          <MatchGroupLink
            matchGroupId={matchData.matchGroupId}
            matchGroupName={`Match #${matchData.matchGroupId}`}
          />
        </div>
      : <p>
          <strong>No project assigned</strong>
        </p>
      }
    </PageSection>
  );
};

const SkillsSection = ({
  categories,
  skillsProficiencies,
}: {
  categories: SkillCategoriesEnum[];
  skillsProficiencies: TeamSkillProficiency[];
}) => {
  return (
    <PageSection title='Skills Insights'>
      <H4>Categories</H4>
      <p className='mb-3'>{categories.join(' | ')}</p>
      <H4>Skills & Proficiencies</H4>
      <SkillsProficienciesChunked skillsProficiencies={skillsProficiencies} />
    </PageSection>
  );
};

const SkillsProficienciesChunked = ({
  skillsProficiencies,
}: {
  skillsProficiencies: TeamSkillProficiency[];
}) => {
  const [showAll, setShowAll] = useState(false);
  const toggleShowAll = () => setShowAll((prev) => !prev);
  const topSkills = skillsProficiencies.slice(0, 5);
  const remainingSkills = skillsProficiencies.slice(5);
  return (
    <div className='grid grid-cols-[auto_1fr] gap-x-4'>
      {!showAll &&
        topSkills.map((skill) => (
          <>
            <p key={`${skill.skill} - name`}>
              {skill.skill} ({skill.total} member{skill.total !== 1 ? 's' : ''}):
            </p>
            <SkillsProficiencies
              proficiencyCounts={skill.proficiencyCounts}
              key={`${skill.skill} - proficiency`}
            />
          </>
        ))}
      {showAll &&
        skillsProficiencies.map((skill) => (
          <>
            <p key={`${skill.skill} - name`}>
              {skill.skill} ({skill.total} members):
            </p>
            <SkillsProficiencies
              proficiencyCounts={skill.proficiencyCounts}
              key={`${skill.skill} - proficiency`}
            />
          </>
        ))}
      {skillsProficiencies.length > 5 && (
        <button className='blue-link col-span-2' onClick={toggleShowAll}>
          {showAll ? 'Show less' : `+${remainingSkills.length} more`}
        </button>
      )}
    </div>
  );
};

const SkillsProficiencies = ({
  proficiencyCounts,
}: {
  proficiencyCounts: TeamSkillProficiencyProficiencyCounts;
}) => {
  const countsArray = Object.entries(proficiencyCounts);
  return (
    <div className='flex gap-2'>
      {countsArray.map((proficiency, index) => (
        <>
          <SkillProficiency
            key={proficiency[0]}
            proficiency={proficiency[0]}
            count={proficiency[1]}
          />
          {index < countsArray.length - 1 && <span>|</span>}
        </>
      ))}
    </div>
  );
};

const SkillProficiency = ({ proficiency, count }: { proficiency: string; count: number }) => {
  return (
    <span className='flex items-center gap-1'>
      <UserIcons count={count} size='12' />
      {proficiency}
    </span>
  );
};

const UserIcons = ({ count, size }: { count: number; size: string }) => {
  return (
    <div className='flex'>
      {Array.from({ length: count }).map((_, i) => (
        <Icon key={i} type='user-01' size={size} fill='#A9A9A9' />
      ))}
    </div>
  );
};

const PageSection = ({
  title,
  sectionCtas,
  children,
}: {
  title: string;
  children: React.ReactNode;
  sectionCtas?: {
    text: string;
    variant?: StandardButtonProps['variant'];
    size?: StandardButtonProps['size'];
    onPress?: AriaButtonProps<'button'>['onPress'];
    isDisabled?: boolean;
  }[];
}) => {
  return (
    <div className='mb-6 border p-4 rounded shadow-xs'>
      <div className='flex gap-4 items-center mb-2'>
        <H2>{title}</H2>
        {sectionCtas && sectionCtas.length > 0 && (
          <div className='flex gap-2'>
            {sectionCtas.map((cta, index) => {
              return (
                <Button
                  key={index}
                  {...{ ...cta, variant: cta.variant || 'secondary-color', size: cta.size || 'md' }}
                  isDisabled={cta.isDisabled}
                >
                  {cta.text}
                </Button>
              );
            })}
          </div>
        )}
      </div>
      {children}
    </div>
  );
};

const H1 = ({ children }: { children: React.ReactNode }) => {
  return <h1 className='text-2xl font-bold mb-4'>{children}</h1>;
};
const H2 = ({ children }: { children: React.ReactNode }) => {
  return <h2 className='text-xl font-bold h-fit'>{children}</h2>;
};
const H3 = ({ children }: { children: React.ReactNode }) => {
  return <h3 className='text-lg font-bold'>{children}</h3>;
};
const H4 = ({ children }: { children: React.ReactNode }) => {
  return <h4 className='font-semibold'>{children}</h4>;
};
